import * as React from 'react'
import { supabase } from './supabase'
import type { BookingAction } from './BookingDetails'

type Booking = { source: 'online' | 'manual'; id: string; state: string; updated_at: string }
function returnError(error: unknown) {
  const text = error && typeof error === 'object' && 'message' in error ? String(error.message) : String(error)
  if (text.includes('BOOKING_CHANGED_REFRESH')) return 'This booking has changed. Close and reopen it before marking it as returned.'
  if (text.includes('BOOKING_NOT_RETURNABLE')) return 'Only confirmed or active bookings can be marked as returned. Reopen the booking to see its current status.'
  if (text.includes('ADMIN_REQUIRED')) return 'You need write access to mark a booking as returned.'
  return text
}

export function useBookingReturn(booking: Booking | null, canWrite: boolean, onSaved: () => Promise<void>) {
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const pending = React.useRef(false)
  React.useEffect(() => { setError('') }, [booking?.id])
  // Active hires retain confirmed as their stored lifecycle status.
  const eligible = canWrite && booking?.state === 'confirmed'
  const markReturned = async () => {
    if (!booking || !eligible || pending.current) return
    pending.current = true
    setBusy(true); setError('')
    try {
      const { error } = await supabase.rpc(booking.source === 'online' ? 'admin_mark_live_booking_returned' : 'admin_mark_manual_booking_returned', {
        p_booking_id: booking.id, p_expected_updated_at: booking.updated_at,
      })
      if (error) throw error
      await onSaved()
    } catch (error) { setError(returnError(error)) }
    finally { pending.current = false; setBusy(false) }
  }
  const action: BookingAction | null = eligible ? { label: 'Mark as returned', isDisabled: busy, onClick: () => void markReturned() } : null
  return { action, busy, error }
}
